import { LocationType } from '~/client/graph'
import MapViewItemType from '~/client/src/shared/enums/MapViewItemType'

interface IMapDisplaySource {
  readonly items: readonly {
    readonly sitemapItemId?: string
    readonly isHidden?: boolean
  }[]
}

export type EditableMapView =
  | { readonly kind: 'globe'; readonly map: IMapDisplaySource }
  | { readonly kind: 'sitemap'; readonly map: IMapDisplaySource }

export interface IMapViewAttribute {
  readonly id: string
  readonly name: string
  readonly color: string
  readonly type: LocationType
}

export interface IMapViewPlacement {
  readonly id: string | null
  readonly type: MapViewItemType | null
  readonly assignedId?: string
  readonly coordinates?: unknown
}

export interface IPlanMapViewItemsArgs<
  TAttribute extends IMapViewAttribute,
  TPlacement extends IMapViewPlacement,
> {
  readonly mapView: EditableMapView
  readonly projectId: string
  readonly attributes: readonly TAttribute[]
  readonly sitemapItems: readonly TPlacement[]
  readonly displaySources: readonly IMapDisplaySource[]
  readonly allowedDatalessItemTypes: readonly MapViewItemType[]
}

export type MapViewItemPlan<
  TAttribute extends IMapViewAttribute,
  TPlacement extends IMapViewPlacement,
> =
  | {
      readonly kind: 'existing'
      readonly attribute: TAttribute | null
      readonly placement: TPlacement
    }
  | {
      readonly kind: 'default'
      readonly attribute: TAttribute
      readonly projectId: string
    }

/** Plans persistent editor rows without mutating source domain objects. */
export function planMapViewItems<
  TAttribute extends IMapViewAttribute,
  TPlacement extends IMapViewPlacement,
>({
  mapView,
  projectId,
  attributes,
  sitemapItems,
  displaySources,
  allowedDatalessItemTypes,
}: IPlanMapViewItemsArgs<TAttribute, TPlacement>): MapViewItemPlan<
  TAttribute,
  TPlacement
>[] {
  const assignedItems = indexAssignedItems(sitemapItems)
  const itemIdsWithDisplayData = indexDisplayData(displaySources)
  const displayedItemIds = indexDisplayedItems(mapView.map)
  const plans: MapViewItemPlan<TAttribute, TPlacement>[] = []

  for (const attribute of attributes) {
    const placements = assignedItems.get(attribute.id)
    if (placements) {
      plans.push({
        kind: 'existing',
        attribute,
        placement: selectPlacement(mapView.kind, displayedItemIds, placements),
      })
    } else {
      plans.push({ kind: 'default', attribute, projectId })
    }
  }

  for (const item of sitemapItems) {
    if (
      !item.assignedId &&
      item.type !== null &&
      allowedDatalessItemTypes.includes(item.type) &&
      item.id !== null &&
      itemIdsWithDisplayData.has(item.id)
    ) {
      plans.push({ kind: 'existing', attribute: null, placement: item })
    }
  }

  return plans
}

/**
 * Keyed by location id alone: assignedTo is never repaired, so a location re-kinded across
 * roots lost its pin here and was re-planned as a default, which the next save persisted
 * as a duplicate (STX1-1187).
 */
function indexAssignedItems<TPlacement extends IMapViewPlacement>(
  sitemapItems: readonly TPlacement[],
): Map<string, TPlacement[]> {
  const itemsById = new Map<string, TPlacement[]>()

  for (const item of sitemapItems) {
    const { assignedId } = item
    if (!assignedId) continue
    const items = itemsById.get(assignedId)
    if (items) items.push(item)
    else itemsById.set(assignedId, [item])
  }

  return itemsById
}

function indexDisplayData(
  displaySources: readonly IMapDisplaySource[],
): Set<string> {
  const itemIds = new Set<string>()
  for (const source of displaySources) {
    for (const item of source.items) {
      if (item.sitemapItemId) itemIds.add(item.sitemapItemId)
    }
  }
  return itemIds
}

function indexDisplayedItems(source: IMapDisplaySource): Set<string> {
  const itemIds = new Set<string>()
  for (const item of source.items) {
    if (item.sitemapItemId && !item.isHidden) {
      itemIds.add(item.sitemapItemId)
    }
  }
  return itemIds
}

function selectPlacement<TPlacement extends IMapViewPlacement>(
  mapViewKind: EditableMapView['kind'],
  displayedItemIds: ReadonlySet<string>,
  placements: readonly TPlacement[],
): TPlacement {
  let selected = placements[0]
  for (const placement of placements) {
    if (isDisplayed(mapViewKind, displayedItemIds, placement)) {
      selected = placement
    }
  }
  return selected
}

function isDisplayed(
  mapViewKind: EditableMapView['kind'],
  displayedItemIds: ReadonlySet<string>,
  placement: IMapViewPlacement,
): boolean {
  if (!placement.id) return false
  if (mapViewKind === 'globe') {
    return displayedItemIds.has(placement.id) && !!placement.coordinates
  }
  return displayedItemIds.has(placement.id)
}
